import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function TabsRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()

  // If children have variant/value, use them as tab values. Otherwise use index.
  const tabs = element.children ?? []

  // Find initial value from active tab or first tab
  const defaultValue = tabs.find(t => t.variant === "active")?.id ?? tabs[0]?.id ?? "0"

  const handleValueChange = (value: string) => {
    dispatchEvent(element.id, "change", { value })
  }

  // "outline" variant uses no bg — just border-separated buttons
  const isOutline = element.variant === "outline"

  const listClasses = isOutline
    ? "inline-flex items-center rounded-lg border p-0.5 gap-0.5"
    : "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground"

  return (
    <TabsPrimitive.Root defaultValue={defaultValue} onValueChange={handleValueChange} className={element.className}>
      <TabsPrimitive.List className={cn(listClasses)}>
        {tabs.map((child) => (
          <TabsPrimitive.Trigger
            key={child.id}
            value={child.id}
            className={cn(
              "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
            )}
          >
            <ElementRenderer element={child} />
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  )
}
